import * as THREE from 'three';
import manifest from '../static/native/manifest.json' with { type: 'json' };

async function resource(name) {
  const version = manifest.files.find(file=>file.name===name)?.sha256;
  if (!version) throw new Error(`Unregistered native resource ${name}`);
  const compressed = name.endsWith('.bin') && typeof DecompressionStream !== 'undefined';
  const response = await fetch(`/assets/native/${name}${compressed ? '.gz' : ''}?v=${version.slice(0,16)}`);
  if (!response.ok) throw new Error(`Native resource ${name}: ${response.status}`);
  return compressed ? new Response(response.body.pipeThrough(new DecompressionStream('gzip'))) : response;
}

// The installed renderer stores position, normal and UV as eight float32s,
// plus global uint32 indices. Keep its geometry, rather than the older GLB.
export async function loadNativeCassette(variant = 'cassette') {
  if (!['cassette','assembly'].includes(variant)) throw new Error('Unknown native model');
  const [meta, vertices, indices] = await Promise.all([
    resource(`archive-${variant}.json`).then(r=>r.json()),
    resource(`archive-${variant}.bin`).then(r=>r.arrayBuffer()),
    resource(`archive-${variant}-indices.bin`).then(r=>r.arrayBuffer()),
  ]);
  if (vertices.byteLength % 32 || indices.byteLength % 4) throw new Error('Invalid native mesh alignment');
  const allVertices = new Float32Array(vertices), allIndices = new Uint32Array(indices);
  const group = new THREE.Group();
  for (const entry of meta.meshes) {
    const {vertexStart,vertexCount,start,count} = entry;
    if ((vertexStart+vertexCount)*8>allVertices.length || start+count>allIndices.length) throw new Error('Invalid native mesh bounds');
    const buffer = new THREE.InterleavedBuffer(allVertices.slice(vertexStart*8,(vertexStart+vertexCount)*8),8);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position',new THREE.InterleavedBufferAttribute(buffer,3,0));
    geometry.setAttribute('normal',new THREE.InterleavedBufferAttribute(buffer,3,3));
    geometry.setAttribute('uv',new THREE.InterleavedBufferAttribute(buffer,2,6));
    const localIndices = allIndices.slice(start,start+count);
    for(let i=0;i<localIndices.length;i++) {
      if(localIndices[i]<vertexStart || localIndices[i]>=vertexStart+vertexCount) throw new Error('Invalid native index');
      localIndices[i]-=vertexStart;
    }
    geometry.setIndex(new THREE.BufferAttribute(localIndices,1));
    const pbr = entry.material.pbrMetallicRoughness;
    const material = new THREE.MeshStandardMaterial({roughness:pbr.roughnessFactor,metalness:pbr.metallicFactor,side:THREE.DoubleSide});
    material.color.fromArray(pbr.baseColorFactor);
    material.name = entry.name;
    group.add(new THREE.Mesh(geometry,material));
  }
  return group;
}

export async function loadNativeBakes() {
  const [surfaceBytes, normalBytes, lightBytes] = await Promise.all([
    resource('archive-baked-surface.bin').then(r=>r.arrayBuffer()),
    resource('archive-baked-normal.bin').then(r=>r.arrayBuffer()),
    resource('archive-baked-light.bin').then(r=>r.arrayBuffer()),
  ]);
  if(surfaceBytes.byteLength!==5592404 || normalBytes.byteLength!==262144 || lightBytes.byteLength!==5308416) throw new Error('Unsupported native atlas format');
  const surface = new THREE.DataTexture(new Uint8Array(surfaceBytes,0,1024*1024*4),1024,1024);
  surface.mipmaps=[];
  for(let size=1024,offset=0;size>=1;size>>=1) {
    const length=size*size*4;
    surface.mipmaps.push({data:new Uint8Array(surfaceBytes,offset,length),width:size,height:size});
    offset+=length;
  }
  surface.generateMipmaps=false;
  surface.minFilter=THREE.LinearMipmapLinearFilter;
  const normals = new THREE.DataTexture(new Uint16Array(normalBytes),128,256,THREE.RGBAFormat,THREE.HalfFloatType);
  const light = new THREE.Data3DTexture(new Uint8Array(lightBytes),576,96,24);
  for(const texture of [surface,normals,light]) {
    texture.magFilter=THREE.LinearFilter;
    if(texture!==surface) texture.minFilter=THREE.LinearFilter;
    texture.needsUpdate=true;
  }
  return {surface,normals,light,theme:{value:0},strength:{value:0.35},reveal:{value:0}};
}

// Atlas addressing follows the native six-face layout. Stored surface/light
// RGB is sRGB encoded radiance / 4; normal response is linear RGBA16F.
export function nativeBackingMaterial(bakes) {
  const material = new THREE.MeshBasicMaterial({color:0xffffff,side:THREE.DoubleSide});
  material.onBeforeCompile = shader => {
    Object.assign(shader.uniforms,{nativeSurface:{value:bakes.surface},nativeNormal:{value:bakes.normals},nativeLight:{value:bakes.light},nativeTheme:bakes.theme,nativeStrength:bakes.strength,nativeReveal:bakes.reveal});
    shader.vertexShader = 'varying vec3 nativePosition; varying vec3 nativeDirection;\n' + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\nnativePosition=position; nativeDirection=normal;');
    shader.fragmentShader = `varying vec3 nativePosition; varying vec3 nativeDirection;
      uniform sampler2D nativeSurface; uniform sampler2D nativeNormal; uniform highp sampler3D nativeLight;
      uniform float nativeTheme; uniform float nativeStrength; uniform float nativeReveal;
      vec3 nativeLinear(vec3 c){return mix(c/12.92,pow((c+0.055)/1.055,vec3(2.4)),step(vec3(0.04045),c));}
      vec3 nativeRadiance(){
        vec3 p=nativePosition,n=normalize(nativeDirection),a=abs(n);
        float f=a.x>a.z?(a.x>a.y?(n.x>0.0?0.0:3.0):(n.y>0.0?1.0:4.0)):(a.z>a.y?(n.z>0.0?2.0:5.0):(n.y>0.0?1.0:4.0));
        vec2 uv=vec2((p.x+2.5)/5.0,p.y/3.7),origin=vec2(0.0),size=vec2(448.0);
        if(f==0.0||f==3.0){uv.x=(p.z+0.25)/0.5;origin=vec2(f==0.0?904.0:964.0,0.0);size=vec2(56.0,448.0);}
        else if(f==1.0||f==4.0){uv.y=(p.z+0.25)/0.5;origin=vec2(f==1.0?0.0:452.0,452.0);size=vec2(448.0,56.0);}
        else if(f==5.0){origin=vec2(452.0,0.0);}
        uv=(origin+clamp(uv,0.0,1.0)*(size-1.0)+0.5)/1024.0;
        vec2 oct=n.xy/(a.x+a.y+a.z);
        if(n.z<0.0) oct=(1.0-abs(oct.yx))*(step(vec2(0.0),oct)*2.0-1.0);
        vec2 nuv=((oct*0.5+0.5)*127.0+0.5)/128.0;nuv.y*=0.5;
        vec4 response=mix(texture2D(nativeNormal,nuv),texture2D(nativeNormal,nuv+vec2(0.0,0.5)),nativeTheme);
        vec3 baked=mix(nativeLinear(texture2D(nativeSurface,uv).rgb),nativeLinear(texture2D(nativeSurface,uv+vec2(0.0,0.5)).rgb),nativeTheme)*4.0;
        vec2 field=(clamp(vec2((p.x+9.0)/18.0,(p.z+7.0)/14.0),0.0,1.0)*95.0+0.5)/96.0;
        vec3 lightUv=vec3((f+field.x)/6.0,field.y,(clamp((p.y+0.4)/5.2,0.0,1.0)*23.0+0.5)/24.0);
        vec3 base=baked*response.rgb+vec3(response.a);
        vec3 fieldLight=nativeLinear(texture(nativeLight,lightUv).rgb)*4.0*nativeStrength;
        // Broad area-light approximation in case space. Keep the right side neutral;
        // a constant gold tint erases depth and makes the substrate look painted.
        vec3 toKey=vec3(-3.4,2.8,2.2)-p;
        float irradiance=max(dot(n,normalize(toKey)),0.0)/(1.0+dot(toKey,toKey)*0.16);
        float edgeShade=mix(0.72,1.0,smoothstep(0.0,0.14,min((p.x+2.5)/5.0,(2.5-p.x)/5.0)));
        vec3 detail=base*vec3(0.68,0.73,0.77)*edgeShade+vec3(1.0,0.72,0.35)*irradiance*2.4;
        return mix(base+fieldLight,detail,nativeTheme*nativeReveal);
      }
    ` + shader.fragmentShader;
    shader.fragmentShader = shader.fragmentShader.replace('#include <color_fragment>','#include <color_fragment>\ndiffuseColor.rgb = nativeRadiance();');
  };
  material.customProgramCacheKey=()=> 'rhine-native-backing-v2';
  return material;
}

// Thin-shell coverage follows viewing angle. This keeps the print clear through
// the face while retaining a visible rim when inspecting the case obliquely.
export function applyShellFresnel(material) {
  material.onBeforeCompile = shader => {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <opaque_fragment>',
      `float shellFacing = abs(dot(normalize(normal), normalize(vViewPosition)));
       float shellRim = pow(1.0 - shellFacing, 3.0);
       diffuseColor.a *= mix(0.6, 3.2, shellRim);
       #include <opaque_fragment>`
    );
  };
  material.customProgramCacheKey = () => 'rhine-shell-fresnel-v1';
  return material;
}
